import { bisectLeft, bisectRight, ScaleTime } from "d3"
import { CompositePartTraceConfigJSON, TraceDataConfig } from "../Types/Trace"
import { TimeSeriesPageManager } from "./TimeSeriesPageManager"
import { Page } from "./Page"

export type TraceData = TimeSeriesData | CompositeTimeSeriesData

export type TimeSeriesData = {
    times: number[]
    data: Float32Array
}

export type CompositeTimeSeriesData = {
    times: number[]
    data: Float32Array[]
}

export function getSignal(traceData: TraceData, config: TraceDataConfig): Float32Array {
    if (config.isCompositePart && isCompositeTimeSeriesData(traceData)) {
        return traceData.data[config.compositeIndex]
    }

    return (traceData as TimeSeriesData).data
}

export function getTimeSeriesData(traceData: TimeSeriesData | CompositeTimeSeriesData, config: TraceDataConfig): TimeSeriesData {
    if (config.isCompositePart && isCompositeTimeSeriesData(traceData)) {
        return { times: traceData.times, data: traceData.data[config.compositeIndex] }
    }

    return traceData as TimeSeriesData
}

export function getGapIndexes(gapIndexes: number[] | number[][], config: TraceDataConfig): number[] {
    if (config.isCompositePart && Array.isArray(gapIndexes[0])) {
        return gapIndexes[config.compositeIndex] as number[]
    }

    return gapIndexes as number[]
}

export function isCompositeTimeSeriesData(traceData: TimeSeriesData | CompositeTimeSeriesData): traceData is CompositeTimeSeriesData {
    return Array.isArray(traceData.data[0]) || traceData.data[0] instanceof Float32Array;
}

export function findIndexOfClosestValidData(timeSeriesData: TimeSeriesData | CompositeTimeSeriesData, maximumTime: number, samplingPeriodMs: number, compositeIndex?: number): number {
    const minimumTime = maximumTime - 2*samplingPeriodMs
    const times = timeSeriesData.times
    let mostRelevantIndex = -1
    let values: Float32Array

    if (isCompositeTimeSeriesData(timeSeriesData)) {
        if (compositeIndex === undefined || compositeIndex < 0 || compositeIndex >= timeSeriesData.data.length) {
            return -1
        }

        values = timeSeriesData.data[compositeIndex]
    } else {
        values = timeSeriesData.data
    }

    for (let i = times.length-1; i >= 0; i--) {
        const time = times[i]

        // Skip values that are undefined or NaN
        if (values[i] === undefined || values[i] === null || isNaN(values[i])) { 
            continue
        }

        // If the data is defined, it is the closest to the target time.
        if (time >= minimumTime && time <= maximumTime) {
            mostRelevantIndex = i
            break
        }

        // If we passed the valid data window, stop early.
        if (time < minimumTime) {
            return -1
        }
    }

    return mostRelevantIndex
}

export function getRealtimeValue(dataObjectId: number, dataKey: string, pageManager: TimeSeriesPageManager<any>, traceConfig: TraceDataConfig, viewScale: ScaleTime<any, any, any>): number | undefined {
    const [currentPage, lastPage] = pageManager.getPagesInView()
    const endTime = viewScale.domain()[1].getTime()
    
    let currentValue: number | undefined = undefined

    for (const page of [lastPage, currentPage]) {
        if (!page || !dataObjectId || !dataKey) {
            continue
        }

        const timeSeriesData = page.data.get(dataObjectId)?.get(dataKey)

        if (!timeSeriesData) {
            continue
        }

        const times = timeSeriesData.times

        if (!times || times.length === 0) {
            continue
        }

        let samplingPeriod: number | undefined = page.timingInformation.get(dataObjectId)?.get(dataKey)?.samplingPeriod

        if (!samplingPeriod && times.length === 1) {
            // It's okay to not have a sampling period when there's only one data point. 
            // We can't calculate the sampling period, but we can still show the realtime value.
            // Just let the sampling period be the page size.
            samplingPeriod = endTime - viewScale.domain()[0].getTime()
        }

        if (!samplingPeriod) {
            continue
        }

        const isComposite = isCompositeTimeSeriesData(timeSeriesData)

        if (isComposite) {
            const trace = traceConfig as CompositePartTraceConfigJSON
            const mostRelevantIndex = findIndexOfClosestValidData(timeSeriesData, endTime, samplingPeriod, trace.compositeIndex)
            const values = timeSeriesData.data[trace.compositeIndex] as Float32Array
            currentValue = values[mostRelevantIndex]
        } else {
            const mostRelevantIndex = findIndexOfClosestValidData(timeSeriesData, endTime, samplingPeriod)
            currentValue = timeSeriesData.data[mostRelevantIndex] as number
        }

        if (currentValue !== undefined) {
            break // We already have a value! No need to check on the previous page.
        }
    }

    return currentValue
}


export function getDataInView(dataObjectId: number, pageManger: TimeSeriesPageManager<Page<any, any>>, traceConfig: TraceDataConfig, viewScale: ScaleTime<any, any, any>, allowMissingValues: boolean = false): number[] {
    const [startDate, endDate] = viewScale.domain()
    const startTime = startDate.getTime()
    const endTime = endDate.getTime()
    const pages = pageManger.getPagesInView()

    let result: number[] = []

    // for each page, aggregate the signal from each page, with timestamps, sorted
    for (let i = 0; i < pages.length; i++) {
        const page = pages[i]

        if (!page) {
            continue
        }

        const traceData = page.data.get(dataObjectId)?.get(traceConfig.dataKey)

        if (!traceData) {
            continue
        }

        const startIndex = bisectLeft(traceData.times, startTime)
        const endIndex = bisectRight(traceData.times, endTime)

        let chunk = getSignal(traceData, traceConfig).slice(startIndex, endIndex)

        if (!allowMissingValues) {
            chunk = chunk.filter(value => !Number.isNaN(value))
        }

        result = [...result, ...chunk]
    }

    return result
}
